// Schreibende Aktionen des Dienstleister-Bereichs. Fehler werden gemeldet (Exception), nie verschluckt.

import { supabase } from '../supabase/client';
import { kanaeleAusSpalten, kanaeleMitPrimaer, ladeKanaele, speichereKanaele, type KontaktKanal } from '../kontakt/kontaktKanaele';
import { objektName } from '../immobilien/objektKopf';
import { setzeObjektLink } from './api';
import type { ObjektRoh } from './liste';
import { kategorieDifferenz, loeschPruefung, type Verknuepfung } from './stammdaten';
import { setzeTicketStatus, type TicketStatusQuelle } from '../tickets/status';
import { erstelleTicket } from '../tickets/erstelleTicket';
import { markAsBezahlt, respondTechPruefung } from '../rechnungen/workflow';
import { erfasseFreigabe } from '../eigentuemer/aktionen';
import { ladeMieterDokumentHoch } from '../mieter/mieterDokumente';
import { statusFuerStufe } from './auftraege';
import type { TicketStufe } from './detail';

const pruefe = (r: { error: { message: string } | null }) => { if (r.error) throw new Error(r.error.message); };

// ── Kategorien (Gewerke) ───────────────────────────────────────────────────────

export async function legeKategorieAn(name: string): Promise<void> {
  pruefe(await supabase.from('dienstleister_kategorien').insert({ name: name.trim(), ist_handwerk: false, sortierung: 500, aktiv: true }));
}

export async function aendereKategorie(id: string, felder: { ist_handwerk?: boolean; aktiv?: boolean }): Promise<void> {
  pruefe(await supabase.from('dienstleister_kategorien').update(felder).eq('id', id));
}

/** Nur aufrufen, wenn kategorieLoeschbar() (keine Dienstleister, keine Regeln). */
export async function loescheKategorieEndgueltig(id: string): Promise<void> {
  pruefe(await supabase.from('dienstleister_kategorien').delete().eq('id', id));
}

// ── Ticket-Regeln ──────────────────────────────────────────────────────────────

export interface RegelZeile {
  id: string;
  name: string | null;
  stichwoerter: string[];
  kategorie_id: string | null;
  gewerk_id: string | null;
  prioritaet: string;
  ist_notfall: boolean;
  aktiv: boolean;
  sortierung: number | null;
  nur_mit_anhang: boolean;
  bearbeiter_team_id: string | null;
  ist_rechtlich_sensibel: boolean;
}
export interface Auswahl { id: string; name: string; aktiv?: boolean | null }

export async function ladeRegelDaten(): Promise<{ regeln: RegelZeile[]; ticketKategorien: Auswahl[]; teams: Auswahl[] }> {
  const [r, k, t] = await Promise.all([
    supabase.from('dienstleister_regeln').select('*').order('sortierung').order('name'),
    supabase.from('ticket_kategorien').select('id, name, aktiv').order('sortierung').order('name'),
    supabase.from('teams').select('id, name').order('name'),
  ]);
  for (const x of [r, k, t]) if (x.error) throw new Error(x.error.message);
  return {
    regeln: ((r.data ?? []) as RegelZeile[]).map(x => ({ ...x, stichwoerter: x.stichwoerter ?? [] })),
    ticketKategorien: (k.data ?? []) as Auswahl[],
    teams: (t.data ?? []) as Auswahl[],
  };
}

export async function speichereRegel(id: string | null, payload: Record<string, string | boolean | string[] | null>): Promise<void> {
  if (id) pruefe(await supabase.from('dienstleister_regeln').update(payload).eq('id', id));
  else {
    // neue Regeln hinten anstellen (kleinste sortierung gewinnt in der E-Mail-Analyse)
    const { data, error } = await supabase.from('dienstleister_regeln').select('sortierung').order('sortierung', { ascending: false }).limit(1);
    if (error) throw new Error(error.message);
    const max = ((data ?? [])[0] as { sortierung: number | null } | undefined)?.sortierung ?? 0;
    pruefe(await supabase.from('dienstleister_regeln').insert({ ...payload, sortierung: max + 10 }));
  }
}

export async function setzeRegelAktiv(id: string, aktiv: boolean): Promise<void> {
  pruefe(await supabase.from('dienstleister_regeln').update({ aktiv }).eq('id', id));
}

export async function loescheRegel(id: string): Promise<void> {
  pruefe(await supabase.from('dienstleister_regeln').delete().eq('id', id));
}

// ── Neuer Dienstleister (Drehkarte) ────────────────────────────────────────────

/**
 * Legt Dienstleister, Kategorien, Nachweise und Kontaktkanäle an. Schlägt ein Folgeschritt fehl,
 * bleibt der Dienstleister bestehen – die Warnung wird zurückgegeben (wie bei Eigentümern).
 */
export async function legeDienstleisterAn(
  payload: Record<string, string | number | boolean | null>,
  kategorieIds: readonly string[],
  nachweise: readonly { art: string; gueltig_bis: string }[],
): Promise<{ id: string; warnungen: string[] }> {
  const { data, error } = await supabase.from('dienstleister').insert(payload).select('id').single();
  if (error || !data) throw new Error(error?.message ?? 'Dienstleister konnte nicht angelegt werden');
  const id = (data as { id: string }).id;
  const warnungen: string[] = [];
  if (kategorieIds.length) {
    const r = await supabase.from('dienstleister_kategorie').insert(kategorieIds.map(k => ({ dienstleister_id: id, kategorie_id: k })));
    if (r.error) warnungen.push(`Kategorien nicht gespeichert: ${r.error.message}`);
  }
  if (nachweise.length) {
    const { data: { user } } = await supabase.auth.getUser();
    const r = await supabase.from('dienstleister_nachweise').insert(nachweise.map(n => ({ ...n, dienstleister_id: id, created_by: user?.id ?? null })));
    if (r.error) warnungen.push(`Nachweise nicht gespeichert: ${r.error.message}`);
  }
  const kanaele: KontaktKanal[] = [];
  if (typeof payload.email === 'string') kanaele.push({ kanal: 'email', wert: payload.email, label: null, ist_primaer: true, sortierung: 0 });
  if (typeof payload.telefon === 'string') kanaele.push({ kanal: 'telefon', wert: payload.telefon, label: null, ist_primaer: true, sortierung: 0 });
  if (typeof payload.notdienst_telefon === 'string') kanaele.push({ kanal: 'telefon', wert: payload.notdienst_telefon, label: 'Notdienst', ist_primaer: !payload.telefon, sortierung: 1 });
  try { if (kanaele.length) await speichereKanaele(supabase, 'dienstleister', id, kanaele); }
  catch (e) { warnungen.push(`Kontaktkanäle nicht gespeichert: ${e instanceof Error ? e.message : 'Fehler'}`); }
  return { id, warnungen };
}

// ── Detail-Panels (E5): Stammdaten, Status, Löschen, Konditionen, Bank, Gewerke, Ansprechpartner, Objekte ──

export async function ladeDlKanaele(d: { id: string } & Record<string, unknown>): Promise<KontaktKanal[]> {
  const k = await ladeKanaele(supabase, 'dienstleister', d.id);
  return k.length ? k : kanaeleAusSpalten(d);
}

/** Spalten speichern, E-Mail/Telefon über die Kontaktkanäle (weitere Kanäle bleiben erhalten). */
export async function speichereDlStammdaten(id: string, payload: Record<string, string | null>, kanaele: readonly KontaktKanal[], email: string, telefon: string): Promise<KontaktKanal[]> {
  pruefe(await supabase.from('dienstleister').update(payload).eq('id', id));
  const neu = kanaeleMitPrimaer(kanaele, email, telefon);
  await speichereKanaele(supabase, 'dienstleister', id, neu);
  return neu;
}

export async function aktualisiereDl(id: string, felder: Record<string, string | number | boolean | null>): Promise<void> {
  pruefe(await supabase.from('dienstleister').update(felder).eq('id', id));
}

/** Frische Prüfung vor dem Löschen (laufende Zuordnung, offenes Ticket, offene Rechnung). */
export async function pruefeDlLoeschen(id: string, heute: string): Promise<Verknuepfung[]> {
  const [zu, ti, re] = await Promise.all([
    supabase.from('dienstleister_objekt').select('objekt_id, aktiv, bis, vertragsart').eq('dienstleister_id', id),
    supabase.from('tickets').select('ticket_nummer, titel, status').eq('dienstleister_id', id).is('geloescht_am', null),
    supabase.from('rechnungen').select('rechnungsnummer, brutto, status').eq('dienstleister_id', id).or('richtung.is.null,richtung.eq.eingang'),
  ]);
  for (const r of [zu, ti, re]) pruefe(r);
  const zuordnungen = (zu.data ?? []) as { objekt_id: string; aktiv: boolean | null; bis: string | null; vertragsart: string | null }[];
  const ids = [...new Set(zuordnungen.map(z => z.objekt_id))];
  const namen = ids.length ? ((await supabase.from('objekte').select('*').in('id', ids)).data ?? []) as ObjektRoh[] : [];
  return loeschPruefung({
    zuordnungen, objektName: oid => { const o = namen.find(n => n.id === oid); return o ? objektName(o) : 'Objekt'; },
    tickets: (ti.data ?? []) as { ticket_nummer: string | null; titel: string | null; status: string | null }[],
    rechnungen: ((re.data ?? []) as { rechnungsnummer: string | null; brutto: number | string | null; status: string | null }[]).map(r => ({ ...r, brutto: r.brutto === null ? null : Number(r.brutto) })),
    heute,
  });
}

/**
 * Löscht nur ohne Verknüpfungen (erneute Prüfung). Kategorien, beendete Zuordnungen, Nachweise, Ansprechpartner und
 * Bewertungen gehen per Kaskade mit; Kontaktwege, Notizen und Logs werden entfernt; bezahlte Rechnungen,
 * Tickets, Termine und Dokumente bleiben ohne Zuordnung erhalten.
 */
export async function loescheDl(id: string, heute: string): Promise<void> {
  if ((await pruefeDlLoeschen(id, heute)).length) throw new Error('Der Dienstleister hat noch Verknüpfungen');
  pruefe(await supabase.from('dienstleister_objekt').delete().eq('dienstleister_id', id));
  pruefe(await supabase.from('kontakt_notizen').delete().eq('quelle', 'dienstleister').eq('quelle_id', id));
  pruefe(await supabase.from('kontakt_aktivitaeten').delete().eq('quelle', 'dienstleister').eq('quelle_id', id));
  pruefe(await supabase.from('kontakt_kanaele').delete().eq('owner_type', 'dienstleister').eq('owner_id', id));
  pruefe(await supabase.from('dienstleister').delete().eq('id', id));
}

/** Gewerke per Mengen-Differenz. */
export async function setzeKategorien(id: string, alt: readonly string[], neu: readonly string[]): Promise<void> {
  const { hinzu, weg } = kategorieDifferenz(alt, neu);
  if (hinzu.length) pruefe(await supabase.from('dienstleister_kategorie').insert(hinzu.map(k => ({ dienstleister_id: id, kategorie_id: k }))));
  if (weg.length) pruefe(await supabase.from('dienstleister_kategorie').delete().eq('dienstleister_id', id).in('kategorie_id', weg));
}

export async function legeKontaktAn(id: string, k: { name: string; rolle: string; telefon: string; email: string }): Promise<void> {
  const leer = (s: string) => (s.trim() ? s.trim() : null);
  pruefe(await supabase.from('dienstleister_kontakte').insert({ dienstleister_id: id, name: k.name.trim(), rolle: k.rolle, telefon: leer(k.telefon), email: leer(k.email) }));
}

export async function loescheKontakt(kontaktId: string): Promise<void> {
  pruefe(await supabase.from('dienstleister_kontakte').delete().eq('id', kontaktId));
}

/** Objekt zuordnen (bestehendes setzeObjektLink) plus Vertragsfelder. */
export async function ordneObjektZu(id: string, objektId: string, z: { von: string; bis: string; vertragsart: string; kostenJahr: number | null; umlagefaehig: boolean }): Promise<void> {
  await setzeObjektLink(id, objektId, z.von || null, z.bis || null, z.vertragsart || null);
  pruefe(await supabase.from('dienstleister_objekt').update({ kosten_jahr: z.kostenJahr, umlagefaehig: z.umlagefaehig, kuendigung_zum: null })
    .eq('dienstleister_id', id).eq('objekt_id', objektId));
}

// ── E6: Aufträge, Rechnungen, Bewertung ────────────────────────────────────────

async function nutzer(): Promise<{ id: string | null; rolle: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { id: null, rolle: null };
  const { data } = await supabase.from('profiles').select('role').eq('id', user.id).maybeSingle();
  return { id: user.id, rolle: (data as { role: string | null } | null)?.role ?? null };
}

/** Ticket eine Stufe weiter (gemeinsame Ticket-Logik: Status, Historie, Benachrichtigung). */
export async function setzeTicketStufe(ticket: TicketStatusQuelle & { beauftragt_am: string | null }, stufe: TicketStufe): Promise<void> {
  const { id } = await nutzer();
  const z = statusFuerStufe(stufe, ticket.beauftragt_am, new Date().toISOString());
  await setzeTicketStatus(ticket, z.status, { benutzerId: id, grund: z.grund, zusatz: z.zusatz });
}

export interface AuftragEntwurf { objektId: string | null; text: string; wunschtermin: string; kostenrahmen: number | null; angebot: boolean }

/**
 * Legt das Ticket über die bestehende Ticket-Logik an (erstelleTicket) und ordnet den Dienstleister zu.
 * Versendet wird anschließend im Ticket über „Handwerker beauftragen“ bzw. „Angebot anfragen“.
 */
export async function erteileAuftrag(dlId: string, a: AuftragEntwurf): Promise<{ id: string; ticket_nummer: string }> {
  const { id: von } = await nutzer();
  const titel = a.text.trim().split('\n')[0]!.slice(0, 90);
  const beschreibung = [a.text.trim(), a.kostenrahmen !== null ? `Kostenrahmen: ${a.kostenrahmen.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}` : ''].filter(Boolean).join('\n\n');
  const t = await erstelleTicket({
    titel, beschreibung, objekt_id: a.objektId ?? undefined, prioritaet: 'mittel', faellig_am: a.wunschtermin || null, dienstleister_vorschlag_id: dlId,
  }, von);
  pruefe(await supabase.from('tickets').update({ dienstleister_id: dlId }).eq('id', t.id));
  return t;
}

/** Eigentümer des Objekts für eine Freigabe-Anfrage (nur bei genau einem laufenden Eigentümer, nicht bei WEG). */
export async function freigabeEmpfaenger(objektId: string, heute: string): Promise<{ eigentuemerId: string; name: string } | { weg: true } | null> {
  const [o, oe] = await Promise.all([
    supabase.from('objekte').select('verwaltungsart, objekttyp').eq('id', objektId).maybeSingle(),
    supabase.from('objekt_eigentuemer').select('eigentuemer_id, vertrag_bis, eigentuemer:eigentuemer_id(vorname, nachname, firma_name)').eq('objekt_id', objektId),
  ]);
  pruefe(o); pruefe(oe);
  const art = `${(o.data as { verwaltungsart: string | null } | null)?.verwaltungsart ?? ''} ${(o.data as { objekttyp: string | null } | null)?.objekttyp ?? ''}`.toLowerCase();
  if (art.includes('weg')) return { weg: true };
  const laufend = ((oe.data ?? []) as unknown as { eigentuemer_id: string; vertrag_bis: string | null; eigentuemer: { vorname: string | null; nachname: string | null; firma_name: string | null } | null }[])
    .filter(r => !r.vertrag_bis || r.vertrag_bis >= heute);
  if (laufend.length !== 1) return null;
  const e = laufend[0]!;
  return { eigentuemerId: e.eigentuemer_id, name: (e.eigentuemer?.firma_name || [e.eigentuemer?.vorname, e.eigentuemer?.nachname].filter(Boolean).join(' ')).trim() || 'Eigentümer' };
}

export async function frageFreigabeAn(eigentuemerId: string, f: { objektId: string; beschreibung: string; betrag: number; dienstleister: string; dienstleisterId: string }): Promise<void> {
  await erfasseFreigabe(eigentuemerId, { objektId: f.objektId, einheitId: null, beschreibung: f.beschreibung, betrag: f.betrag, dienstleister: f.dienstleister, dienstleisterId: f.dienstleisterId }, false);
}

/** offen → sachlich geprüft (bestehende technische Prüfung im Rechnungsbereich). */
export async function pruefeRechnung(rechnungId: string): Promise<void> {
  const { id } = await nutzer();
  if (!id) throw new Error('Nicht angemeldet');
  await respondTechPruefung(rechnungId, id, 'freigegeben', 'Sachlich geprüft im Dienstleister-Bereich');
}

/** geprüft → bezahlt (bestehendes markAsBezahlt inkl. Vier-Augen-Prinzip ab 3.000 €). */
export async function bezahleRechnung(r: { id: string; brutto: number | null; freigegeben: boolean | null }): Promise<void> {
  const { id, rolle } = await nutzer();
  if (!id) throw new Error('Nicht angemeldet');
  await markAsBezahlt(r.id, id, r.brutto ?? 0, r.freigegeben === true, rolle ?? undefined);
}

export async function erfasseRechnung(payload: Record<string, string | number | boolean | null>, beleg: { datei: File; dlName: string } | null): Promise<string[]> {
  const { data, error } = await supabase.from('rechnungen').insert(payload).select('id').single();
  if (error || !data) {
    if (error?.code === '23505') throw new Error('Diese Rechnungsnummer gibt es für den Dienstleister schon');
    throw new Error(error?.message ?? 'Rechnung konnte nicht gespeichert werden');
  }
  const warnungen: string[] = [];
  if (beleg) {
    try {
      const dok = await ladeMieterDokumentHoch({ datei: beleg.datei, mieterId: String(payload.dienstleister_id), mieterName: beleg.dlName, objektId: null, kategorie: 'Rechnung', ordner: 'dienstleister', dienstleister: true });
      pruefe(await supabase.from('rechnungen').update({ dokument_id: dok.id }).eq('id', (data as { id: string }).id));
    } catch (e) { warnungen.push(`Beleg nicht angehängt: ${e instanceof Error ? e.message : 'Fehler'}`); }
  }
  return warnungen;
}

export async function speichereBewertung(dlId: string, b: { sterne: number; kommentar: string; ticketId: string | null; kriterien: Record<string, number> }): Promise<void> {
  const { id } = await nutzer();
  pruefe(await supabase.from('dienstleister_bewertungen').insert({
    dienstleister_id: dlId, sterne: b.sterne, kommentar: b.kommentar.trim() || null, ticket_id: b.ticketId, kriterien: b.kriterien, created_by: id,
  }));
}

// ── E7: Verträge, Nachweise ────────────────────────────────────────────────────

/** Kündigung zum Termin (Vertrag bleibt bis dahin aktiv) bzw. zurücknehmen (null). */
export async function setzeKuendigung(dlId: string, objektId: string, zum: string | null): Promise<void> {
  pruefe(await supabase.from('dienstleister_objekt').update({ kuendigung_zum: zum }).eq('dienstleister_id', dlId).eq('objekt_id', objektId));
}

export async function erfasseNachweis(dlId: string, dlName: string, art: string, gueltigBis: string | null, datei: File | null): Promise<string[]> {
  const { id } = await nutzer();
  const warnungen: string[] = [];
  let dokumentId: string | null = null;
  if (datei) {
    try {
      dokumentId = (await ladeMieterDokumentHoch({ datei, mieterId: dlId, mieterName: dlName, objektId: null, kategorie: 'Nachweis', ordner: 'dienstleister', dienstleister: true })).id;
    } catch (e) { warnungen.push(`Datei nicht hochgeladen: ${e instanceof Error ? e.message : 'Fehler'}`); }
  }
  pruefe(await supabase.from('dienstleister_nachweise').insert({ dienstleister_id: dlId, art, gueltig_bis: gueltigBis, dokument_id: dokumentId, created_by: id }));
  return warnungen;
}
